// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { shadowRoot } from '@iheartjane/reefer';
import { spacing } from '@iheartjane/reefer-emotion';
import type { MouseEventHandler, ReactNode } from 'react';

import {
  SKIP_TO_RESULTS_ID,
  useHasSkipToResultsTarget,
  useScrollToNode,
} from '@jane/shared-ecomm/hooks';

const Link = styled.a({
  ...spacing({ px: 24, py: 8 }),
  background: 'white',
  zIndex: 'var(--layers-toast)',
  position: 'absolute',
  left: '-10000px',
  top: 'auto',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  '&:focus': {
    position: 'fixed',
    left: 0,
    width: 'auto',
    height: 'auto',
  },
});

interface SkipLinkProps {
  children: ReactNode;
  getTarget: () => HTMLElement | null;
  targetElementId: string;
}

// Boost renders the app inside a shadow root.
const appRoot = () => shadowRoot() || document;

// A few pages (home, checkout) have no <main>, only #content.
const getMainContent = () =>
  appRoot().querySelector<HTMLElement>('main') ??
  appRoot().getElementById('content');

const getResults = () => appRoot().getElementById(SKIP_TO_RESULTS_ID);

const forceFocus = (element: HTMLElement) => {
  element.focus({ preventScroll: true });
  if (document.activeElement !== element) {
    element.setAttribute('tabindex', '-1');
    element.focus({ preventScroll: true });
  }
  return;
};

const SkipLink = ({ getTarget, targetElementId, children }: SkipLinkProps) => {
  const scrollToNode = useScrollToNode();

  const handleClick: MouseEventHandler = (e) => {
    e.preventDefault();
    const element = getTarget();

    if (!element) return;
    scrollToNode(element);
    forceFocus(element);
  };

  return (
    <Link onClick={handleClick} href={`#${targetElementId}`}>
      {children}
    </Link>
  );
};

/** Main content starts with any filters; results skips past them. */
export const SkipLinks = () => {
  const hasResults = useHasSkipToResultsTarget();

  return (
    <>
      <SkipLink targetElementId="content" getTarget={getMainContent}>
        Skip to main content
      </SkipLink>
      {hasResults && (
        <SkipLink targetElementId={SKIP_TO_RESULTS_ID} getTarget={getResults}>
          Skip to results
        </SkipLink>
      )}
    </>
  );
};
